'use client';

import { useTranslations } from 'next-intl';
import { GameData } from '@/features/games/shared/types/game-data';
import { ActionButtonWithDescription } from '@/shared/components/action-button-with-description';
import { useEffect } from 'react';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { useAppStore } from '@/features/shared/app-store';
import Loader from '@/shared/components/loader';
import { useUser } from '@/features/auth/shared/hooks/use-user';

import { isAlreadyInCart } from '@/features/games/shared/helpers/is-already-in-cart';
import { useMembershipTokenStore } from '@/features/membership/shared/state/membership-token-store';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { selectGamePrice } from '@/features/games/shared/store/game-price-slice-selectors';
import { mapGamePrices } from '@/features/games/shared/helpers/map-game-prices';

type Props = {
  gameData: GameData;
};

export const PurchaseGameByTokenButton = ({ gameData }: Props) => {
  const t = useTranslations('PurchaseGameByToken');

  const { loading, authenticatedUser } = useUser();
  const isMember = authenticatedUser?.isWildMember || false;
  const items = useAppStore((s) => s.cart.items);
  const showOverlay = useOverlayStore((state) => state.showOverlay);
  const fetchTokens = useMembershipTokenStore((s) => s.fetchTokens);
  const tokensInitialized = useMembershipTokenStore((s) => s.initialized);

  const availableTokens = useMembershipTokenStore((s) => s.tokenSummary?.tokensAvailable ?? 0);
  const prices = useGameStore(selectGamePrice(gameData.sku));
  const tokenPrice = prices && mapGamePrices(prices, true).tokens?.[0]?.value;
  const buyOption = gameData.purchaseOptions?.find((opt) => opt.type === 'buy');
  //@TODO add initialized states in stores and add depencencies here WTDEV-620
  const isLoading = !(tokensInitialized && !loading);
  const isRedeemable = buyOption && !isAlreadyInCart(items, buyOption);
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchTokens();
      } catch (err) {
        console.error('Error loading purchase data', err);
      }
    };
    if (!loading && authenticatedUser && authenticatedUser.isWildMember) {
      fetchData();
    }
  }, [loading, authenticatedUser, gameData, items, fetchTokens]);

  const handleClick = async () => {
    if (availableTokens === 0 || !tokenPrice || availableTokens < tokenPrice) return;
    showOverlay('purchase-game-by-token');
    return;
  };

  if (!isMember || (!isLoading && !isRedeemable)) {
    return null;
  }

  if (isLoading) {
    return (
      <ActionButtonWithDescription
        key='loading-token'
        title={t('useToken')}
        variant='disabled'
        description={<Loader label={t('loadingToken')} />}
        className='min-h-20'
      />
    );
  }
  if (!isMember || (!isLoading && !isRedeemable)) {
    return null;
  }
  return (
    <>
      <ActionButtonWithDescription
        key='token'
        title={t('useToken')}
        description={t('useTokenDescription', { tokenValue: tokenPrice || '' })}
        variant={
          availableTokens > 0 && availableTokens >= tokenPrice! ? 'purchaseToken' : 'disabled'
        }
        onClick={handleClick}
        className='min-h-20'
      />
    </>
  );
};
